
import { GameConstants } from "./GameConstants";
import { GameVars } from "./GameVars";
import { AudioManager } from "./AudioManager";
import mapsData from "../assets/config/maps.json";
import * as seedrandom from "seedrandom";

export class GameManager {

    public static init(): void {

        GameVars.mapsData = mapsData;

        if (GameVars.currentScene.sys.game.device.os.desktop) {
            GameVars.scaleY = 1;
            GameVars.scaleCorrectionFactor = 1;
        } else {

            GameVars.currentScene.game.scale.displaySize = GameVars.currentScene.game.scale.parentSize;
            GameVars.currentScene.game.scale.refresh();

            const aspectRatio = window.innerHeight / window.innerWidth;
            GameVars.scaleY = (GameConstants.GAME_HEIGHT / GameConstants.GAME_WIDTH) / aspectRatio;

            if (aspectRatio > 1.5) {
                GameVars.scaleCorrectionFactor = 1.2;
            } else if (aspectRatio < 1.33) {
                GameVars.scaleCorrectionFactor = .8;
            } else {
                GameVars.scaleCorrectionFactor = 1;
            }
        }

        const date = new Date();
        const seed = GameManager.hashCode(date.getDay() + "/" + date.getMonth() + "/" + date.getFullYear());

        GameVars.seedRandom = seedrandom.default(seed);

        GameManager.readGameData();
    }

    public static readGameData(): void {

        GameManager.getGameStorageData(
            GameConstants.SAVED_GAME_DATA_KEY,
            function (gameData: string): void {

                if (gameData) {
                    GameVars.gameData = JSON.parse(gameData);
                } else {
                    GameVars.gameData = {
                        soundMuted: false,
                        musicMuted: false,
                        language: GameConstants.LANGUAGE_ENGLISH,
                        scores: [],
                        tutorialSeen: false
                    };
                }

                GameManager.startGame(new Date().getDate() - 1);
            }
        );
    }

    public static setCurrentScene(scene: Phaser.Scene): void {

        GameVars.currentScene = scene;
    }

    public static onGameAssetsLoaded(): void {

        AudioManager.init();

        GameVars.gameText = GameVars.currentScene.cache.json.get("game-text");

        GameManager.enterSplashScene();
        // GameManager.enterBattleScene();
    }

    public static enterSplashScene(): void {

        GameVars.currentScene.scene.start("SplashScene");
    }

    public static enterBattleScene(): void {

        GameVars.currentScene.scene.start("BattleScene");
    }

    public static reset(): void {

        if (GameVars.gameOver) {
            GameManager.enterSplashScene();
        } else {
            GameManager.enterBattleScene();
        }
    }

    public static writeGameData(): void {

        GameManager.setGameStorageData(
            GameConstants.SAVED_GAME_DATA_KEY,
            GameVars.gameData,
            function (): void {
                GameManager.log("game data successfully saved");
            }
        );
    }

    public static log(text: string, error?: Error): void {

        if (!GameConstants.VERBOSE) {
            return;
        }

        if (error) {
            console.error(text + ":", error);
        } else {
            console.log(text);
        }
    }

    public static getTimeUntilEndOfDay(): number {

        const d = new Date();
        const h = d.getHours();
        const m = d.getMinutes();
        const s = d.getSeconds();

        return (24 * 60 * 60) - (h * 60 * 60) - (m * 60) - s;
    }

    public static generateObstaclesBoard(): void {

        GameVars.obstaclesBoard = [];

        for (let i = 0; i < 22; i++) {

            GameVars.obstaclesBoard[i] = [];
            for (let j = 0; j < 14; j++) {
                GameVars.obstaclesBoard[i][j] = 0;
            }
        }

        let rand = (Math.floor(GameVars.seedRandom() * 5) + 1);
        let prevRand = rand;

        for (let i = 0; i < 22; i++) {
            for (let j = 0; j < 14; j++) {

                let c = j - 1;
                let r = i - 8;

                if (GameVars.seedRandom() < .1) {
                    rand = (Math.floor(GameVars.seedRandom() * 4) + 6);
                }

                if (r < 0 || c === -1 || c === 12) {

                    if (GameVars.enemiesPathCells[0].r === -1) {
                        if ((c > GameVars.enemiesPathCells[0].c + 1 || c < GameVars.enemiesPathCells[0].c - 1) || (r > GameVars.enemiesPathCells[0].r || r < GameVars.enemiesPathCells[0].r - 1)) {
                            GameVars.obstaclesBoard[i][j] = rand;
                        }
                    } else {
                        if (GameVars.enemiesPathCells[0].r + 1 !== r || GameVars.enemiesPathCells[0].c !== c) {
                            GameVars.obstaclesBoard[i][j] = rand;
                        }
                    }
                }

                rand = prevRand;
            }
        }

        for (let i = 0; i < GameVars.plateausCells.length; i++) {

            if (GameVars.seedRandom() < .1) {
                rand = (Math.floor(GameVars.seedRandom() * 4) + 6);
            }

            GameVars.obstaclesBoard[GameVars.plateausCells[i].r + 8][GameVars.plateausCells[i].c + 1] = rand;

            rand = prevRand;
        }

        for (let i = 0; i < GameVars.obstaclesBoard.length; i++) {
            for (let j = 0; j < GameVars.obstaclesBoard[0].length; j++) {

                if (GameVars.obstaclesBoard[i][j] === 0) {
                    continue;
                }

                let values = [];
                values.push({i: i, j: j});

                if (i !== 0 && GameVars.obstaclesBoard[i - 1][j] !== 0) {
                    values.push({i: i - 1, j: j});
                }

                if (i !== GameVars.obstaclesBoard.length - 1 && GameVars.obstaclesBoard[i + 1][j] !== 0) {
                    values.push({i: i + 1, j: j});
                }

                if (j !== 0 && GameVars.obstaclesBoard[i][j - 1] !== 0) {
                    values.push({i: i, j: j - 1});
                }

                if (j !== GameVars.obstaclesBoard[0].length - 1 && GameVars.obstaclesBoard[i][j + 1] !== 0) {
                    values.push({i: i, j: j + 1});
                }

                if (i !== 0 && j !== 0 && GameVars.obstaclesBoard[i - 1][j - 1] !== 0) {
                    values.push({i: i - 1, j: j - 1});
                }

                if (i !== 0 && j !== GameVars.obstaclesBoard[0].length - 1 && GameVars.obstaclesBoard[i - 1][j + 1] !== 0) {
                    values.push({i: i - 1, j: j + 1});
                }

                if (i !== GameVars.obstaclesBoard.length - 1 && j !== 0 && GameVars.obstaclesBoard[i + 1][j - 1] !== 0) {
                    values.push({i: i + 1, j: j - 1});
                }

                if (i !== GameVars.obstaclesBoard.length - 1 && j !== GameVars.obstaclesBoard[0].length - 1 && GameVars.obstaclesBoard[i + 1][j +  1] !== 0) {
                    values.push({i: i + 1, j: j + 1});
                }

                if (values.length >= 4 && GameVars.seedRandom() < .1) {

                    for (let k = 0; k < values.length; k++) {
                        GameVars.obstaclesBoard[values[k].i][values[k].j] = 10;
                    }
                }
            }
        }
    }

    private static startGame(mapIndex: number): void {

        GameVars.currentMapId = mapIndex;

        if (GameVars.gameData.scores.length === 0) {
            for (let i = 0; i < 31; i++) {
                GameVars.gameData.scores[i] = 0;
            }
        }

        GameVars.currentMapData = GameVars.mapsData[mapIndex];
        GameVars.enemiesPathCells = GameVars.currentMapData.path;
        GameVars.plateausCells = GameVars.currentMapData.plateaus;

        GameManager.generateObstaclesBoard();

        GameVars.currentScene.scene.start("PreloadScene");
    }

    private static getGameStorageData(key: string, successCb: Function): void {

        const gameDataStr = localStorage.getItem(key);
        successCb(gameDataStr);
    }

    private static setGameStorageData(key: string, value: any, successCb: Function): void {

        localStorage.setItem(key, JSON.stringify(value));
        successCb();
    }

    private static hashCode(s: string): string {
        
        return s.split("").reduce(function(a, b) {a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0).toString();              
    }
}
